import React, { useState, useEffect, useRef } from 'react'
import { Check, ChevronDown, X, Search } from 'lucide-react'

export default function SearchableSelect({ options, value, onChange, placeholder = 'Select…', allLabel = 'All', searchPlaceholder = 'Search…', disabled = false, triggerClassName = '' }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    if (open) { setQuery(''); const t = setTimeout(() => inputRef.current?.focus(), 0); return () => clearTimeout(t) }
  }, [open])

  const selectedLabel = value ? (options.find(o => String(o.value) === String(value))?.label || value) : allLabel
  const q = query.trim().toLowerCase()
  const filtered = q ? options.filter(o => (o.label || '').toLowerCase().includes(q)) : options
  const pick = (v) => { onChange(v); setOpen(false) }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        className={`flex items-center justify-between gap-2 h-9 px-3 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 min-w-[180px] dark:bg-gray-800 dark:text-gray-200 bg-white text-left w-full disabled:opacity-50 disabled:cursor-not-allowed ${triggerClassName}`}
      >
        <span className={`truncate flex-1 ${value ? 'text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>{selectedLabel}</span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {value && (
            <span onClick={(e) => { e.stopPropagation(); onChange('') }} className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors cursor-pointer" title="Clear">
              <X className="h-3 w-3 text-gray-400" />
            </span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-2 min-w-[220px] w-max max-w-[340px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full h-8 pl-8 pr-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto p-1.5 space-y-0.5">
            <button
              type="button"
              onClick={() => pick('')}
              className={`flex items-center justify-between w-full px-2.5 py-2 rounded-lg text-sm text-left transition-colors ${!value ? 'bg-navy-50 dark:bg-navy-900/30 text-navy-700 dark:text-navy-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
            >
              <span className="truncate">{allLabel}</span>
              {!value && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
            </button>
            {filtered.length === 0 ? (
              <div className="px-2.5 py-4 text-center text-xs text-gray-400 dark:text-gray-500">No matches found</div>
            ) : filtered.map((o) => {
              const isSel = String(o.value) === String(value)
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => pick(o.value)}
                  className={`flex items-center justify-between gap-2 w-full px-2.5 py-2 rounded-lg text-sm text-left transition-colors ${isSel ? 'bg-navy-50 dark:bg-navy-900/30 text-navy-700 dark:text-navy-300' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                >
                  <span className="truncate">{o.label}</span>
                  {isSel && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
